import React, { createContext, useContext, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { auth } from '@/lib/picasso-firebase';

interface WebSocketMessage {
  event: string;
  video_id?: string;
  timestamp?: string;
  data?: {
    content_type?: 'video' | 'image' | 'transcript';
    sender_role?: 'admin' | 'user';
    sender_email?: string;
    user_email?: string;
    video_info?: any;
    image_info?: any;
    video_id?: string;
    message?: string;
    type?: string;
    video_data?: any;
    [key: string]: any;
  };
  message?: string;
  user_email?: string;
  video_info?: any;
  status?: string;
  room_type?: string;
  identifier?: string;
}

type MessageHandler = (data: WebSocketMessage) => void;

interface WebSocketContextType {
  isConnected: boolean;
  connectionError: string | null;
  subscribe: (id: string, handler: MessageHandler) => void;
  unsubscribe: (id: string) => void;
  sendMessage: (message: any) => void;
  pinVideoWebSocket: (videoId: string, userEmail?: string) => void;
  unpinVideoWebSocket: (videoId: string, userEmail?: string) => void;
  reconnect: () => void;
  userEmail: string | null;
  isAdmin: boolean;
  roomType: 'user' | 'admin' | null;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children
}) => {
  const location = useLocation();
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;
  const baseReconnectDelay = 2000;
  const KEEP_ALIVE_INTERVAL = 20000; // 20 seconds
  const CONNECTION_TIMEOUT = 10000; // 10 seconds

  // Authentication and connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roomType, setRoomType] = useState<'user' | 'admin' | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Store message handlers
  const messageHandlers = useRef<Map<string, MessageHandler>>(new Map());

  // Determine room type based on user role
  const determineRoomType = useCallback((userIsAdmin: boolean): 'user' | 'admin' => {
    // Admin users: always connect to admin room
    if (userIsAdmin) {
      return 'admin';
    }
    // Content creators: always connect to user room
    return 'user';
  }, []);

  // Initialize authentication state once
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserEmail(user.email);

        try {
          const token = await user.getIdToken();
          const response = await fetch(`${import.meta.env.VITE_BASE_URL}/picasso/auth/me`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            const userIsAdmin = userData.role === 'admin';
            setIsAdmin(userIsAdmin);
            
            // Set room type based on user role
            const targetRoom = determineRoomType(userIsAdmin);
            setRoomType(targetRoom);
            
          }
        } catch (error) {
          console.error('Failed to fetch user role:', error);
          setIsAdmin(false);
          setRoomType('user');
        }
      } else {
        setUserEmail(null);
        setIsAdmin(false);
        setRoomType(null);
      }
      setAuthInitialized(true);
    });

    return unsubscribe;
  }, [determineRoomType]);

  // Cleanup functions
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const startKeepAlive = useCallback(() => {
    stopKeepAlive();

    keepAliveIntervalRef.current = setInterval(() => {
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        try {
          websocketRef.current.send(JSON.stringify({ type: 'ping' }));
        } catch (error) {
          console.error('Failed to send ping:', error);
          
        }
      }
    }, KEEP_ALIVE_INTERVAL);
  }, [KEEP_ALIVE_INTERVAL, stopKeepAlive]);

  // Helper function to execute message handlers
  const executeHandlers = useCallback((data: WebSocketMessage) => {
    for (const handler of messageHandlers.current.values()) {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    }
  }, []);

  // Helper functions to reduce duplicacy
  const isContentReviewPath = useCallback((path: string) => {
    return path === '/content-review' || path === '/admin/content-review';
  }, []);

  const isDashboardPath = useCallback((path: string) => {
    return path === '/dashboard';
  }, []);

  const isUserOwnContent = useCallback((data: WebSocketMessage, userEmail: string | null) => {
    const senderEmail = data.data?.sender_email || data.data?.user_email;
    return senderEmail === userEmail;
  }, []);

  const isGenerationEvent = useCallback((eventType: string) => {
    return ['video_generated', 'video_uploaded', 'image_generated'].includes(eventType);
  }, []);

  const isPinEvent = useCallback((eventType: string) => {
    return ['video_pinned', 'video_unpinned'].includes(eventType);
  }, []);

  const shouldExecuteHandlers = useCallback((
    condition: boolean,
    data: WebSocketMessage,
    logContext?: string
  ) => {
    if (condition) {
      executeHandlers(data);
      if (logContext) {
        console.debug(`✅ WebSocket Debug - ${logContext}:`, data);
      }
    }
  }, [executeHandlers]);

  // Helper function to handle generation/upload events
  const handleGenerationEvents = useCallback((data: WebSocketMessage) => {
    const currentPath = location.pathname;
    
    // Rule 3: Content creator gets personal dynamic update (except admin on content-review)
    if (isUserOwnContent(data, userEmail)) {
      // Rule 4: If admin generated content and is on content-review, skip dynamic update
      if (isAdmin && isContentReviewPath(currentPath)) {
        return; // Admin on content-review gets popup instead, not dynamic update
      }
      
      // Rule 8: Filter by content type and current page (using page level as specified)
      // Only send updates if user is on dashboard (where content is displayed)
      shouldExecuteHandlers(
        isDashboardPath(currentPath),
        data,
        "Generation event for user on dashboard"
      );
    }
  }, [location.pathname, userEmail, isAdmin, isUserOwnContent, isContentReviewPath, isDashboardPath, shouldExecuteHandlers]);

  // Helper function to handle admin popup notifications
  const handleAdminPopup = useCallback((data: WebSocketMessage) => {
    const currentPath = location.pathname;
    // Rule 6 & 7: Admins get popup only if on content-review page
    shouldExecuteHandlers(
      isAdmin && isContentReviewPath(currentPath),
      data,
      "Admin popup on content-review"
    );
  }, [location.pathname, isAdmin, isContentReviewPath, shouldExecuteHandlers]);

  // Helper function to handle admin pin/unpin events
  const handleAdminPinEvents = useCallback((data: WebSocketMessage) => {
    const currentPath = location.pathname;
    const videoUserEmail = data.data?.user_email;
    
    // Admin on content-review page: sees ALL pin/unpin activities
    if (isContentReviewPath(currentPath)) {
      shouldExecuteHandlers(true, data, "Admin sees all pin/unpin on content-review");
      return;
    }
    
    // Admin on dashboard page: ONLY sees their OWN video activities
    shouldExecuteHandlers(
      isDashboardPath(currentPath) && videoUserEmail === userEmail,
      data,
      "Admin sees own video pin/unpin on dashboard"
    );
  }, [location.pathname, userEmail, isContentReviewPath, isDashboardPath, shouldExecuteHandlers]);

  // Helper function to handle user pin/unpin events
  const handleUserPinEvents = useCallback((data: WebSocketMessage) => {
    const videoUserEmail = data.data?.user_email;
    
    // Non-admin users only see pin/unpin events for their own videos
    shouldExecuteHandlers(
      videoUserEmail === userEmail,
      data,
      "User sees own video pin/unpin"
    );
  }, [userEmail, shouldExecuteHandlers]);

  const broadcastMessage = useCallback((data: WebSocketMessage) => {
    // Support both 'event' and 'type' fields for WebSocket messages
    const messageType = data?.event || (data as any)?.type;
    
    // Validate that we have a proper event/type before processing
    if (!messageType) {
      return;
    }
    
    // Handle video generation lifecycle events (new messages with 'type' field)
    if (messageType === 'video_generation_started' || 
        messageType === 'video_generated' || 
        messageType === 'video_generation_cancelled' ||
        messageType === 'video_generation_failed') {
      // These are direct user notifications, broadcast to all subscribers
      executeHandlers(data);
      return;
    }
    
    // Handle generation/upload events (video_generated, video_uploaded, image_generated)
    if (isGenerationEvent(messageType)) {
      handleGenerationEvents(data);
      return;
    }
    
    // Handle admin popup notifications
    if (messageType === 'admin_video_popup') {
      handleAdminPopup(data);
      return;
    }
    
    // Handle pin/unpin events for admin users
    if (isAdmin && isPinEvent(messageType)) {
      handleAdminPinEvents(data);
      return;
    }
    
    // Handle pin/unpin events for non-admin users
    if (isPinEvent(messageType)) {
      handleUserPinEvents(data);
      return;
    }
    
    // Default case: broadcast to all subscribers (non-admin users or other events)
    executeHandlers(data);
  }, [isAdmin, isGenerationEvent, isPinEvent, handleGenerationEvents, handleAdminPopup, handleAdminPinEvents, handleUserPinEvents, executeHandlers]);  // Main connection function - only creates ONE connection that persists
  const connect = useCallback(async (targetRoomType?: 'user' | 'admin') => {
    // Prevent multiple simultaneous connections
    if (websocketRef.current?.readyState === WebSocket.OPEN || 
        websocketRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Don't connect if we don't have auth data yet
    if (!userEmail || !authInitialized) {
      return;
    }

    const effectiveRoomType = targetRoomType || roomType;
    if (!effectiveRoomType) return;

    try {
      const wsUrl = `${import.meta.env.VITE_WS_URL}/picasso/ws/video-updates?room_type=${effectiveRoomType}&identifier=${userEmail}`;
      
      websocketRef.current = new WebSocket(wsUrl);

      // Connection timeout
      const timeoutId = setTimeout(() => {
        if (websocketRef.current?.readyState === WebSocket.CONNECTING) {
          websocketRef.current.close();
          setConnectionError('Connection timeout');
        }
      }, CONNECTION_TIMEOUT);

      websocketRef.current.onopen = () => {
        clearTimeout(timeoutId);
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        clearReconnectTimeout();
        startKeepAlive();
        
        // Update room type to match actual connection
        if (targetRoomType) {
          setRoomType(targetRoomType);
        }
        
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle ping/pong for keep-alive
          if (data.type === 'ping') {
            websocketRef.current?.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (data.type === 'pong') {
            return;
          }

          // Broadcast to all subscribers
          broadcastMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      websocketRef.current.onclose = (event) => {
        clearTimeout(timeoutId);
        setIsConnected(false);
        stopKeepAlive();

        // Only attempt reconnection for unexpected disconnections
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.current);
          reconnectAttempts.current++;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect(effectiveRoomType);
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setConnectionError('Max reconnection attempts reached');
          console.error('WebSocket max reconnection attempts reached');
        }
      };

      websocketRef.current.onerror = (error) => {
        clearTimeout(timeoutId);
        console.error('❌ WebSocket Debug - Connection error:', {
          error: error,
          roomType: effectiveRoomType,
          identifier: userEmail,
          readyState: websocketRef.current?.readyState
        });
        setConnectionError('WebSocket connection error');
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionError('Failed to create WebSocket connection');
    }
  }, [userEmail, authInitialized, roomType, clearReconnectTimeout, broadcastMessage, startKeepAlive, stopKeepAlive, CONNECTION_TIMEOUT, isAdmin]);

  // Clean disconnect function
  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    stopKeepAlive();
    
    if (websocketRef.current) {
      websocketRef.current.close(1000, 'Manual disconnect');
      websocketRef.current = null;
    }
    setIsConnected(false);
  }, [clearReconnectTimeout, stopKeepAlive]);

  // Optimized message sending
  const sendMessage = useCallback((message: any) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      const messageString = typeof message === 'string' ? message : JSON.stringify(message);
      websocketRef.current.send(messageString);
    }
  }, []);

  // Video pinning functions
  const pinVideoWebSocket = useCallback((videoId: string, user_Email?: string) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN && roomType) {
      websocketRef.current.send(JSON.stringify({ 
        event: 'pin_video', 
        video_id: videoId,
        room_type: roomType,
        identifier: userEmail,
        user_email: user_Email
      }));
    }
  }, [roomType, userEmail]);

  const unpinVideoWebSocket = useCallback((videoId: string, user_Email?: string) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN && roomType) {
      websocketRef.current.send(JSON.stringify({ 
        event: 'unpin_video', 
        video_id: videoId,
        room_type: roomType,
        identifier: userEmail,
        user_email: user_Email
      }));
    }
  }, [roomType, userEmail]);

  // Manual reconnection
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttempts.current = 0;
    setConnectionError(null);
    if (roomType) {
      connect(roomType);
    }
  }, [disconnect, connect, roomType]);

  // Subscription management
  const subscribe = useCallback((id: string, handler: MessageHandler) => {
    messageHandlers.current.set(id, handler);
  }, []);

  const unsubscribe = useCallback((id: string) => {
    messageHandlers.current.delete(id);
  }, []);

  // Establish initial connection once auth is ready
  useEffect(() => {
    
    if (authInitialized && userEmail && roomType) {
      connect();
    }
    
    // Cleanup on unmount
    return () => {
      // Only disconnect if there's actually a connection
      if (websocketRef.current) {
        disconnect();
      }
    };
  }, [authInitialized, userEmail, roomType]);

  // Reconnect when room type changes (for room switching)
  useEffect(() => {
    
    if (isConnected && roomType && websocketRef.current) {
      // If we're connected but room type changed, reconnect
      const currentUrl = new URL(websocketRef.current.url);
      const currentRoomType = currentUrl.searchParams.get('room_type');
      
      if (currentRoomType !== roomType) {
        connect(roomType);
      }
    }
  }, [roomType, connect]); // Remove isConnected from deps to avoid infinite loop

  const contextValue: WebSocketContextType = useMemo(() => ({
    isConnected,
    connectionError,
    subscribe,
    unsubscribe,
    sendMessage,
    pinVideoWebSocket,
    unpinVideoWebSocket,
    reconnect,
    userEmail,
    isAdmin,
    roomType
  }), [
    isConnected,
    connectionError,
    subscribe,
    unsubscribe,
    sendMessage,
    pinVideoWebSocket,
    unpinVideoWebSocket,
    reconnect,
    userEmail,
    isAdmin,
    roomType
  ]);

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

